import { useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, Plus, Trash2 } from "lucide-react"
import { cn } from "@/lib/utils"
import * as programApi from "../../api/outreach-program.api"
import {
  ALLOWED_WEEKDAYS,
  DAY_NAMES,
  WINDOW_EARLIEST,
  WINDOW_LATEST,
  apiErrorMessage,
  buildConfigPatch,
  formFromProgram,
  formatCentral,
  validateProgramForm,
  type ProgramForm,
} from "../../lib/program"
import type { ProgramStatus } from "../../types/outreach-program.types"

interface Props {
  orgId: string
  program: ProgramStatus
  onSaved: (program: ProgramStatus) => void
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null
  return (
    <p id={id} role="alert" className="text-xs text-destructive">
      {message}
    </p>
  )
}

/**
 * The program's effective settings (as GET /program returns them: defaults
 * applied, windows already clipped) and the form to change them. Save sends
 * only the changed sections, in graham's config shape, via PATCH /program.
 * Status (enable / shadow / pause / disable) lives in ProgramControls.
 */
export function ProgramConfigForm({ orgId, program, onSaved }: Props) {
  const initial = useMemo(() => formFromProgram(program), [program])
  const [form, setForm] = useState<ProgramForm>(initial)
  const [spanishReviewed, setSpanishReviewed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // A new status from the server (save, reload, status change) resets the form.
  useEffect(() => {
    setForm(initial)
    setSpanishReviewed(false)
    setServerError(null)
  }, [initial])

  const set = <K extends keyof ProgramForm>(key: K, value: ProgramForm[K]) => {
    setSaved(false)
    setForm((f) => ({ ...f, [key]: value }))
  }

  const ab = program.reminder_ab
  const g = program.caps?.global
  const errors = validateProgramForm(form, g, ab?.arms)
  const turningSpanishOn = form.spanishEnabled && !initial.spanishEnabled
  if (turningSpanishOn && !spanishReviewed)
    errors.spanish = "Confirm a native speaker has reviewed the Spanish text before turning it on."
  const patch = buildConfigPatch(initial, form)
  const invalid = Object.keys(errors).length > 0

  const save = async () => {
    if (invalid || !patch) return
    setSaving(true)
    setServerError(null)
    try {
      onSaved(await programApi.updateProgramConfig(orgId, patch))
      setSaved(true)
    } catch (err) {
      setServerError(apiErrorMessage(err, "Save failed"))
    } finally {
      setSaving(false)
    }
  }

  const describedBy = (key: string) => (errors[key] ? `err-${key}` : undefined)

  return (
    <div className="space-y-5" data-testid="program-config-form">
      <p className="text-xs text-muted-foreground">
        Effective settings for this clinic, as the program runs them now.
        {program.cadence &&
          ` Retries every ${program.cadence.retry_spacing_days} days, up to ${program.cadence.max_attempts} consent attempts and ${program.cadence.checkin_max_attempts_per_month} check-in attempts a month (not editable here).`}
        {program.updated_by && ` Last changed by ${program.updated_by}${program.updated_at ? ` ${formatCentral(program.updated_at)}` : ""}.`}
      </p>

      <section className="space-y-2">
        <h4 className="text-sm font-medium">Caps</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="cap-day" className="text-xs">
              Daily call cap (this clinic)
            </Label>
            <Input
              id="cap-day"
              type="number"
              min={1}
              max={g?.max_calls_per_day}
              value={form.maxPerDay}
              aria-invalid={!!errors.maxPerDay}
              aria-describedby={describedBy("maxPerDay")}
              onChange={(e) => set("maxPerDay", e.target.value)}
            />
            <FieldError id="err-maxPerDay" message={errors.maxPerDay} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cap-15" className="text-xs">
              Pacing: calls per 15 minutes
            </Label>
            <Input
              id="cap-15"
              type="number"
              min={1}
              max={g?.calls_per_15min}
              value={form.per15}
              aria-invalid={!!errors.per15}
              aria-describedby={describedBy("per15")}
              onChange={(e) => set("per15", e.target.value)}
            />
            <FieldError id="err-per15" message={errors.per15} />
          </div>
        </div>
        {g ? (
          <p className="text-xs text-muted-foreground">
            Global limits (all clinics, not editable here): {g.max_concurrent_outbound} concurrent outbound calls ·{" "}
            {g.max_texts_per_day} texts and {g.max_segments_per_day} segments per day
            {g.calls_per_15min != null && ` · ${g.calls_per_15min} calls per 15 minutes`}
            {g.max_calls_per_day != null && ` · ${g.max_calls_per_day} calls per day`}.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">Global limits: not available.</p>
        )}
      </section>

      <section className="space-y-2">
        <h4 className="text-sm font-medium">Call window (Central)</h4>
        <p className="text-xs text-muted-foreground">
          Inside {WINDOW_EARLIEST}-{WINDOW_LATEST} Central, Monday to Friday only.
        </p>
        {form.windows.map((w, i) => (
          <div key={i} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                aria-label={`Window ${i + 1} start`}
                type="time"
                className="w-32"
                min={WINDOW_EARLIEST}
                max={WINDOW_LATEST}
                value={w.start}
                aria-invalid={!!errors[`window${i}`]}
                onChange={(e) => set("windows", form.windows.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))}
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                aria-label={`Window ${i + 1} end`}
                type="time"
                className="w-32"
                min={WINDOW_EARLIEST}
                max={WINDOW_LATEST}
                value={w.end}
                aria-invalid={!!errors[`window${i}`]}
                onChange={(e) => set("windows", form.windows.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))}
              />
              {form.windows.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove window ${i + 1}`}
                  onClick={() => set("windows", form.windows.filter((_, j) => j !== i))}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
            <FieldError id={`err-window${i}`} message={errors[`window${i}`]} />
          </div>
        ))}
        <FieldError id="err-windows" message={errors.windows} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => set("windows", [...form.windows, { start: "13:00", end: "17:00" }])}
        >
          <Plus /> Add window
        </Button>
        <div className="flex flex-wrap gap-1.5 pt-1" role="group" aria-label="Weekdays">
          {DAY_NAMES.map((name, d) => {
            const allowed = ALLOWED_WEEKDAYS.includes(d)
            const on = form.days.includes(d)
            return (
              <button
                key={name}
                type="button"
                disabled={!allowed}
                aria-pressed={on}
                title={allowed ? undefined : "No outreach on weekends"}
                onClick={() => set("days", on ? form.days.filter((x) => x !== d) : [...form.days, d].sort((a, b) => a - b))}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-xs",
                  on ? "bg-primary text-primary-foreground border-primary" : "bg-card",
                  !allowed && "opacity-40 cursor-not-allowed",
                )}
              >
                {name}
              </button>
            )
          })}
        </div>
        <FieldError id="err-days" message={errors.days} />
      </section>

      <section className="space-y-2">
        <h4 className="text-sm font-medium">Consent reminder A/B</h4>
        {!ab ? (
          <p className="text-xs text-muted-foreground">Reminder A/B: not available.</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              Test tag <span className="font-mono">{ab.tag}</span>. Arm A is the standard text (100% by default); give a
              challenger arm a share to test it. Weights must add up to 100.
            </p>
            <div className="space-y-2">
              {Object.keys(form.weights).map((arm) => (
                <div key={arm} className="grid grid-cols-[3rem,6rem,1fr] items-start gap-2">
                  <span className="pt-2 text-sm font-medium">{arm}</span>
                  <Input
                    aria-label={`Arm ${arm} weight`}
                    type="number"
                    min={0}
                    max={100}
                    value={form.weights[arm]}
                    aria-invalid={!!errors.weights}
                    onChange={(e) => set("weights", { ...form.weights, [arm]: e.target.value })}
                  />
                  <p className="rounded-md bg-muted/40 px-2 py-1.5 text-xs text-muted-foreground whitespace-pre-wrap">
                    {ab.arms[arm] || "(no text)"}
                  </p>
                </div>
              ))}
            </div>
            <FieldError id="err-weights" message={errors.weights} />

            <div className="rounded-md border border-dashed p-2 text-xs space-y-1.5" data-testid="spanish-arm">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="spanish-enabled"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.spanishEnabled}
                  onChange={(e) => set("spanishEnabled", e.target.checked)}
                />
                <Label htmlFor="spanish-enabled" className="text-xs font-medium">
                  Send the Spanish reminder
                </Label>
                <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-warning">
                  needs native review
                </span>
                <span className="text-muted-foreground">
                  {initial.spanishEnabled ? "now enabled" : "now not enabled: Spanish-preference patients get no reminder"}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-muted-foreground">{ab.spanish_arms?.A || "(no Spanish text)"}</p>
              {turningSpanishOn && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={spanishReviewed}
                    onChange={(e) => setSpanishReviewed(e.target.checked)}
                  />
                  A native Spanish speaker has reviewed this text.
                </label>
              )}
              <FieldError id="err-spanish" message={errors.spanish} />
            </div>
          </>
        )}
      </section>

      {serverError && (
        <p role="alert" className="text-sm text-destructive" data-testid="save-error">
          Not saved: {serverError}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={save} disabled={saving || invalid || !patch}>
          {saving && <Loader2 className="animate-spin" />}
          Save settings
        </Button>
        {!patch && !saved && <span className="text-xs text-muted-foreground">No changes</span>}
        {saved && <span className="text-xs text-success">Saved</span>}
        {patch && (
          <Button variant="ghost" size="sm" onClick={() => setForm(initial)}>
            Discard changes
          </Button>
        )}
      </div>
    </div>
  )
}
